// US-072: pure header builder for the Create-PO-from-supplier table.
//
// Turns a resolved create-view column config (per-column { enabled, order })
// plus product-custom-column definitions into the ordered Vuetify header array
// consumed by PurchaseOrderCreate.vue. Import-free (no SupplierModel) so it
// unit-tests without the dateformat ERR_REQUIRE_ESM issue.

import { REORDER_PARAM_COLUMNS } from './reorderParamColumns';

export type CreateHeaderType = 'existing' | 'reorder_param' | 'custom' | 'metafield';

export interface CreateHeader {
  text: string;
  value: string;
  type: CreateHeaderType;
  columnId?: string;
  order: number;
  sortable?: boolean;
  width?: string;
  align?: string;
}

interface ColumnConfig { enabled?: boolean; order?: number; }
interface ResolvedConfig {
  columns?: Record<string, ColumnConfig>;
  metafieldColumns?: Record<string, { enabled?: boolean; order?: number; label?: string }>;
}
interface BuildOpts {
  productColumn: string;
  currSuffix?: string;
  customColumnDefs?: Record<string, { label?: string; enabled?: boolean; order?: number }>;
  /**
   * US-087: true when the active fetch is the full-report flow (fetchProductsViaReportOnly),
   * which can honor a server-side sort on these report-native columns. False (the default) in
   * the two-phase fast flow, where Suggested Qty / Historical Sales / Reorder Date are only
   * known for the current page — sorting the whole catalog by them there would require the
   * slow report call anyway, defeating the fast path's purpose.
   */
  reportOnlySortable?: boolean;
}

/**
 * Descriptor for each existing Create-PO column, keyed by its create-view config
 * id. `required` columns are always shown (editable/identity columns the user
 * cannot remove); the rest are shown only when enabled. `order` is the fallback
 * position when the saved config omits one. `text`/`value` may be derived from
 * runtime context (product column key, currency suffix). This is the single
 * source of truth for the existing columns' identity, order, and required split.
 */
const EXISTING_COLUMNS: Record<string, {
  required: boolean;
  order: number;
  build: (o: BuildOpts) => Omit<CreateHeader, 'order' | 'type'>;
}> = {
  image: { required: false, order: 1, build: () => ({ text: 'Image', value: 'variant_image', sortable: false, width: '80px' }) },
  product: { required: true, order: 2, build: (o) => ({ text: 'Product', value: o.productColumn }) },
  historical_sales: {
    required: false, order: 3,
    build: (o) => ({ text: 'Historical Sales', value: 'historicalSales', sortable: !!o.reportOnlySortable }),
  },
  reorder_date: {
    required: false, order: 4,
    build: (o) => ({ text: 'Reorder Date', value: '[reorder_date]', sortable: !!o.reportOnlySortable }),
  },
  suggested_qty: {
    required: false, order: 5,
    build: (o) => (
      { text: 'Suggested Qty', value: '[inventory_replenishment]', align: 'center', sortable: !!o.reportOnlySortable }
    ),
  },
  quantity: { required: true, order: 6, build: () => ({ text: 'Quantity', value: 'quantity', width: '120px' }) },
  unit_cost: { required: true, order: 7, build: (o) => ({ text: `Unit Cost${o.currSuffix || ''}`, value: '[average_unit_cost]', width: '120px' }) },
  total: { required: true, order: 8, build: (o) => ({ text: `Total${o.currSuffix || ''}`, value: 'total', width: '120px' }) },
};

const REORDER_PARAM_BY_ID = new Map(REORDER_PARAM_COLUMNS.map(c => [c.id, c]));

/**
 * US-087: the header `value`s that only the report-only flow can sort by
 * (historical_sales / reorder_date / suggested_qty above). Exported so
 * PurchaseOrderCreate.vue can fall back to the default sort when a
 * report-only sort is still active on `dataTableOptions` after the view
 * transitions back to the fast flow, where these columns aren't sortable.
 */
export const REPORT_ONLY_SORT_VALUES: ReadonlySet<string> = new Set([
  'historicalSales', '[reorder_date]', '[inventory_replenishment]',
]);

/**
 * Existing create columns as enabled config entries at their default order — the
 * single source shared with defaultCreateViewConfig so the id/order mapping is
 * defined once (here, alongside EXISTING_COLUMNS). Fresh object each call.
 */
export function defaultExistingColumnConfig(): Record<string, { enabled: boolean; order: number }> {
  const out: Record<string, { enabled: boolean; order: number }> = {};
  for (const [id, descriptor] of Object.entries(EXISTING_COLUMNS)) {
    out[id] = { enabled: true, order: descriptor.order };
  }
  return out;
}

const CUSTOM_KEY_PREFIX = 'custom_';
/** Build the create-view config key / header value for a product custom column. */
export function customConfigKey(columnId: string): string { return `${CUSTOM_KEY_PREFIX}${columnId}`; }
/** Extract the custom column id from a `custom_<id>` key, or null if not one. */
export function parseCustomConfigKey(key: string): string | null {
  return key.startsWith(CUSTOM_KEY_PREFIX) ? key.slice(CUSTOM_KEY_PREFIX.length) : null;
}

export function buildCreateHeaders(config: ResolvedConfig, opts: BuildOpts): CreateHeader[] {
  const columns = config?.columns || {};
  const metafieldColumns = config?.metafieldColumns || {};
  const customDefs = opts.customColumnDefs || {};
  const headers: CreateHeader[] = [];

  // Existing columns: required ones always present; removable ones only when enabled.
  for (const [id, descriptor] of Object.entries(EXISTING_COLUMNS)) {
    const cfg = columns[id];
    const enabled = descriptor.required || cfg?.enabled === true;
    if (!enabled) { continue; }
    const order = typeof cfg?.order === 'number' ? cfg.order : descriptor.order;
    headers.push({ ...descriptor.build(opts), type: 'existing', order });
  }

  // Config-driven columns: reorder-param + custom (keyed custom_<id>).
  for (const [id, cfg] of Object.entries(columns)) {
    if (EXISTING_COLUMNS[id]) { continue; }
    if (cfg?.enabled !== true) { continue; }
    const order = typeof cfg.order === 'number' ? cfg.order : 999;

    const rp = REORDER_PARAM_BY_ID.get(id);
    if (rp) {
      // value is the report column code so the row's report data renders directly.
      headers.push({ text: rp.label, value: rp.reportCode, type: 'reorder_param', columnId: rp.id, order, sortable: false });
      continue;
    }

    const customId = parseCustomConfigKey(id);
    if (customId !== null) {
      const def = customDefs[customId];
      headers.push({ text: def?.label || customId, value: customConfigKey(customId), type: 'custom', columnId: customId, order, sortable: false });
      continue;
    }
    // Unknown id: ignore (defensive — server validation should prevent these).
  }

  // Metafield columns.
  for (const [key, cfg] of Object.entries(metafieldColumns)) {
    if (cfg?.enabled !== true) { continue; }
    const order = typeof cfg.order === 'number' ? cfg.order : 999;
    headers.push({ text: cfg.label || key, value: `mf_${key}`, type: 'metafield', columnId: key, order, sortable: false });
  }

  headers.sort((a, b) => a.order - b.order);
  return headers;
}
